import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { formsAPI, authAPI } from '../services/api';
import toast from 'react-hot-toast';
import PageHero from '../components/Common/PageHero';
import DataGrid from '../components/Common/DataGrid';

interface AuditLog {
  id: number;
  fieldName: string;
  oldValue: string;
  newValue: string;
  timestamp: string;
  user: {
    id: number;
    username: string;
    email: string;
  } | null;
  // Set when a service account reported this change on behalf of an email
  // with no linked DataPool Monitor account (see `user` being null above).
  submittedByEmail?: string | null;
  form: {
    id: number;
    customerName: string;
    serviceLine: string;
    customerId?: string;
    kitNumber?: string;
  };
}

interface AuditResponse {
  data: AuditLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

interface AuditUserOption {
  id: number;
  email: string;
  username: string;
}

const PAGE_SIZE = 100;

const AuditTrail: React.FC = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [userId, setUserId] = useState('');
  const [serviceLine, setServiceLine] = useState('');

  const [users, setUsers] = useState<AuditUserOption[]>([]);
  const debounce = useRef<NodeJS.Timeout>();

  // Debounce the free-text search only; other filters apply immediately
  // since they're discrete selections, not per-keystroke input.
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => setSearch(searchInput), 500);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [searchInput]);

  // Any filter change resets back to page 1.
  useEffect(() => {
    setPage(1);
  }, [search, dateFrom, dateTo, userId, serviceLine]);

  useEffect(() => {
    authAPI.getAllUsers()
      .then((data: AuditUserOption[]) => setUsers(data || []))
      .catch(() => {
        // Non-fatal: the user filter just won't have options.
      });
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response: AuditResponse = await formsAPI.getAllAudits({
        page,
        limit: PAGE_SIZE,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        userId: userId ? Number(userId) : undefined,
        serviceLine: serviceLine || undefined,
        search: search || undefined,
      });
      setLogs(response.data || []);
      setTotal(response.pagination?.total || 0);
      setTotalPages(response.pagination?.totalPages || 1);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, dateFrom, dateTo, userId, serviceLine]);

  const canExport = Boolean(dateFrom && dateTo);

  const handleExport = async () => {
    if (!canExport) {
      toast.error('Choose a start and end date before exporting.');
      return;
    }

    setExporting(true);
    const loadingToast = toast.loading('Preparing export... This may take a moment for large datasets.');
    try {
      const blob = await formsAPI.exportAudit({
        dateFrom,
        dateTo,
        userId: userId ? Number(userId) : undefined,
        serviceLine: serviceLine || undefined,
        search: search || undefined,
      });
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_trail_${dateFrom}_to_${dateTo}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Audit trail exported successfully 📄');
    } catch (err: any) {
      console.error('Export error:', err);
      const status = err.response?.status;
      // The request uses responseType: 'blob' to receive the .xlsx file on
      // success — but that means an *error* JSON response also arrives as a
      // Blob, not a parsed object. Read it back out as text before using it.
      let info: any = err.response?.data;
      if (info instanceof Blob) {
        try {
          info = JSON.parse(await info.text());
        } catch {
          info = undefined;
        }
      }

      if (status === 400) {
        toast.error(info?.message || 'A start and end date are required to export.');
      } else if (status === 404) {
        toast.error('No audit logs match these filters.');
      } else if (status === 429) {
        toast.error('Export failed: Rate limit exceeded. Please wait a moment and try again.');
      } else if (err.code === 'ECONNABORTED') {
        toast.error('Export timed out. Try narrowing your date range or filters.');
      } else if (status === 500) {
        toast.error('Export failed: Server error. Please try again later.');
      } else {
        toast.error('Export failed. Please try again.');
      }
    } finally {
      toast.dismiss(loadingToast);
      setExporting(false);
    }
  };

  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = total === 0 ? 0 : Math.min(page * PAGE_SIZE, total);

  const userOptions = useMemo(
    () => [...users].sort((a, b) => a.email.localeCompare(b.email)),
    [users],
  );

  return (
    <div className="space-y-8">
      <PageHero
        badge={{ label: 'Audit and compliance', tone: 'violet' }}
        title="Audit Trail"
        description="Review service-form changes, who made them, and the exact before/after values."
        stats={[
          { icon: 'fas fa-layer-group', label: `${total} matching records` },
        ]}
        actions={
          <>
            <button
              onClick={() => {
                if (window.history.length > 1) navigate(-1);
                else navigate('/users');
              }}
              className="rounded-2xl bg-white/10 px-6 py-3 font-semibold text-white hover:bg-white/15"
            >
              Back
            </button>
            <button
              onClick={handleExport}
              disabled={!canExport || exporting}
              title={!canExport ? 'Choose a start and end date to export' : undefined}
              className="rounded-2xl bg-white px-6 py-3 font-semibold text-slate-900 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {exporting ? 'Exporting…' : 'Export'}
            </button>
          </>
        }
      />

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">From date</label>
            <input
              type="date"
              className="w-full rounded-xl bg-white px-4 py-2.5 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-fuchsia-500/30"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">To date</label>
            <input
              type="date"
              className="w-full rounded-xl bg-white px-4 py-2.5 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-fuchsia-500/30"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">User</label>
            <select
              className="w-full rounded-xl bg-white px-4 py-2.5 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-fuchsia-500/30"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            >
              <option value="">All users</option>
              {userOptions.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.email}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Service line</label>
            <input
              type="text"
              className="w-full rounded-xl bg-white px-4 py-2.5 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-fuchsia-500/30"
              placeholder="e.g. SL-5096332"
              value={serviceLine}
              onChange={(e) => setServiceLine(e.target.value)}
            />
          </div>
        </div>

        <input
          type="text"
          className="w-full rounded-2xl bg-white px-5 py-3 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-fuchsia-500/30 transition-all"
          placeholder="Search by user, customer, service line, kit number, or field…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />

        {!canExport && (
          <p className="text-xs text-amber-600">
            <i className="fas fa-circle-info mr-1"></i>
            Choose a start and end date above to enable exporting — this keeps exports fast on a large table.
          </p>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <DataGrid
          title="Audit logs"
          description={`${total} matching record${total === 1 ? '' : 's'}`}
          rows={logs}
          emptyText="No audit logs match the current filters."
          loading={loading}
          minWidthClassName="min-w-[1000px]"
          columns={[
            { key: 'timestamp', header: 'Timestamp', render: (log) => new Date(log.timestamp).toLocaleString() },
            { key: 'user', header: 'User', render: (log) => log.user ? `${log.user.username} (${log.user.email})` : log.submittedByEmail ? `${log.submittedByEmail} (no account)` : 'System' },
            { key: 'serviceLine', header: 'Service Line', render: (log) => log.form.serviceLine },
            { key: 'customerId', header: 'Customer ID', render: (log) => log.form.customerId || '—' },
            { key: 'kitNumber', header: 'Kit Number', render: (log) => log.form.kitNumber || '—' },
            { key: 'field', header: 'Field', render: (log) => log.fieldName },
            { key: 'oldValue', header: 'Old Value', render: (log) => <span className="text-red-600">{log.oldValue || '—'}</span> },
            { key: 'newValue', header: 'New Value', render: (log) => <span className="font-semibold text-green-700">{log.newValue}</span> },
          ]}
        />

        {total > 0 && (
          <div className="flex flex-col gap-3 border-t border-gray-200 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-gray-600">
              Page {page}/{totalPages} - records {rangeStart}-{rangeEnd} of {total}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page <= 1}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page >= totalPages}
                className="rounded-lg bg-violet-600 px-3 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditTrail;
